import { useEffect, useState } from 'react';
import { 
  isEmailLinkSignIn, 
  completeEmailLinkSignIn, 
  getStoredEmailForSignIn,
  clearStoredEmailForSignIn,
  getPostSignInRedirectPath,
  clearPostSignInRedirectPath,
} from '../../auth/firebase';
import apiService from '../../services/api';
import { logger } from '../../utils/logger';
import '../../styles.css';

interface EmailLinkCallbackProps {
  onSuccess: (redirectPath?: string) => void;
  onError: (redirectPath?: string) => void;
}

type CallbackState = 'processing' | 'needEmail' | 'success' | 'error';

const EmailLinkCallback = ({ onSuccess: _onSuccess, onError }: EmailLinkCallbackProps) => {
  const [state, setState] = useState<CallbackState>('processing');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const resolveRedirectPath = (): string => {
    const url = new URL(window.location.href);
    const redirectParam = url.searchParams.get('redirect');
    return redirectParam || getPostSignInRedirectPath() || '/dashboard';
  };

  useEffect(() => {
    const processEmailLink = async () => {
      // Check if this is an email link sign-in
      if (!isEmailLinkSignIn()) {
        setError('Invalid sign-in link. Please request a new one.');
        setState('error');
        return;
      }

      // Try to get the stored email
      const storedEmail = getStoredEmailForSignIn();
      
      if (storedEmail) {
        await completeSignIn(storedEmail);
      } else {
        // Email not found - ask user to enter it
        setState('needEmail');
      }
    };

    processEmailLink();
  }, []);

  const completeSignIn = async (signInEmail: string) => {
    try {
      setState('processing');
      logger.log('[EmailLinkCallback] Starting email link sign-in');
      
      const { user, idToken } = await completeEmailLinkSignIn(signInEmail);
      logger.log('[EmailLinkCallback] Firebase auth succeeded');
      
      // Set auth token for API calls
      apiService.setAuthToken(idToken);
      logger.log('[EmailLinkCallback] Set auth token');
      
      // Initialize user in the system using fetch (through nginx proxy)
      // This ensures the request goes through the same path as other API calls
      try {
        logger.log('[EmailLinkCallback] Calling /api/users/initialize...');
        const initResponse = await fetch('/api/users/initialize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            email: user.email || signInEmail,
            display_name: user.displayName || signInEmail.split('@')[0],
            firebase_uid: user.uid
          })
        });
        
        logger.log('[EmailLinkCallback] initializeUser response status:', initResponse.status);
        
        if (initResponse.ok) {
          logger.log('[EmailLinkCallback] initializeUser succeeded');
        } else {
          // Log status only, no sensitive data
          logger.error('[EmailLinkCallback] initializeUser failed with status:', initResponse.status);
        }
      } catch (initError: any) {
        // User initialization failed, but Firebase auth succeeded
        // Log error without sensitive details
        logger.error('[EmailLinkCallback] User initialization network error');
        // Continue anyway - user will see pending page and admin will need to check logs
      }
      
      setState('success');
      clearStoredEmailForSignIn();
      const redirectPath = resolveRedirectPath();
      clearPostSignInRedirectPath();
      
      // Redirect after short delay so auth state can settle and App routing can resume normally.
      setTimeout(() => {
        _onSuccess(redirectPath);
      }, 1500);
      
    } catch (err: any) {
      logger.error('[EmailLinkCallback] Sign-in error:', err.message);
      setError(err.message || "We couldn't complete your sign-in. Please request a new link and try again.");
      setState('error');
      clearStoredEmailForSignIn();
      clearPostSignInRedirectPath();
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      await completeSignIn(email);
    }
  };

  const handleBackToSignIn = () => {
    const redirectPath = resolveRedirectPath();
    clearPostSignInRedirectPath();
    onError(redirectPath);
  };

  if (state === 'processing') {
    return (
      <div className="email-callback-container">
        <div className="callback-card">
          <div className="spinner"></div>
          <h2>Signing you in...</h2>
          <p>Please wait while we verify your sign-in link.</p>
        </div>
      </div>
    );
  }

  if (state === 'needEmail') {
    return (
      <div className="email-callback-container">
        <div className="callback-card">
          <h2>Confirm Your Email</h2>
          <p>Please enter the email address you used to sign in:</p>
          
          <form onSubmit={handleEmailSubmit} className="email-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              autoFocus
            />
            <button type="submit" className="wizrdom-button primary">
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="email-callback-container">
        <div className="callback-card success">
          <div className="success-icon">✓</div>
          <h2>Sign-in Successful!</h2>
          <p>Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="email-callback-container">
      <div className="callback-card error">
        <div className="error-icon">✕</div>
        <h2>Sign-in Failed</h2>
        <p>{error}</p>
        <button 
          className="wizrdom-button primary"
          onClick={handleBackToSignIn}
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );
};

export default EmailLinkCallback;
